/**
 * Navgar Messenger — embeddable widget bundle.
 *
 * Vanilla DOM (no framework deps) — keeps the bundle small and avoids
 * polluting host pages with React/Preact runtimes. Built into an IIFE
 * (`dist/messenger-embed.js`) and served from
 * `${apiBase}/api/embed/messenger.js`.
 *
 * Host usage:
 *   <script src="https://api.navgar.com/api/embed/messenger.js"></script>
 *   <script>
 *     NavgarMessenger.boot({ apiBase, user_id?, user_hash?, email?, name? });
 *   </script>
 *
 * Anonymous mode: omit user_id/user_hash. The widget will collect email
 * via inline magic-link signin when the user tries to send a message.
 *
 * Identified mode: pass user_id + user_hash (HMAC-SHA256 of user_id with
 * MESSENGER_HMAC_SECRET, hex) so the widget skips signin entirely.
 */

interface BootOptions {
  apiBase?: string;
  user_id?: string | number;
  user_hash?: string;
  email?: string;
  /**
   * Optional second server-side signature covering the email:
   *   email_hash = HMAC_SHA256(secret, `${user_id}:${lowercase(email)}`)
   * When present and valid, the backend may safely link a pre-existing
   * account with the same email (e.g. from an earlier magic-link signin)
   * to this identity. Without it, an email collision shows the sign-in form.
   */
  email_hash?: string;
  name?: string;
  company?: string;
  locale?: "en" | "es";
  /**
   * Host environment name, e.g. "staging", "dev", "qa". Any value other than
   * "production"/"prod" shows a visible badge on the launcher and panel header
   * and prefixes created case subjects with [ENV] so support can tell test
   * traffic apart. Omit (or pass "production") on the live site.
   */
  environment?: string;
  /**
   * Hide the default floating launcher bubble. Use when the host app has its
   * own "open support" button — call NavgarMessenger.open() / .close() from it.
   */
  hideLauncher?: boolean;
}

interface MeResponse {
  authenticated: boolean;
  user?: { id: number; email: string; name: string; displayName: string | null; avatarUrl: string | null };
  identifiedModeConfigured?: boolean;
}

interface ConversationSummary {
  id: number;
  code: string;
  subject: string;
  status: string;
  updatedAt: string;
  preview: string;
  unread: boolean;
}

interface Message {
  id: number;
  role: "customer" | "agent" | "system" | "bot";
  content: string;
  createdAt: string;
  isMine: boolean;
}

interface MessengerAPI {
  boot: (opts?: BootOptions) => Promise<void>;
  shutdown: () => Promise<void>;
  open: () => void;
  close: () => void;
}

const HOST_ID = "navgar-messenger-host";
const PANEL_W = 380;
const PANEL_H = 600;

const COPY = {
  en: {
    greeting: "Hi {name} 👋",
    greetingAnon: "Hi 👋",
    subline: "How can we help?",
    askQuestion: "Ask a question",
    askSub: "AI Agent and team can help",
    searchPlaceholder: "Search for help",
    tabHome: "Home",
    tabMessages: "Messages",
    tabHelp: "Help",
    newConvo: "New conversation",
    emailCapture: "Enter your email to continue",
    emailPlaceholder: "you@example.com",
    namePlaceholder: "Your name (optional)",
    companyPlaceholder: "Company (optional)",
    sendLink: "Send me a link",
    checkEmail: "Check your email — we sent you a sign-in link.",
    composerPlaceholder: "Type your message…",
    send: "Send",
    noConvos: "No conversations yet. Start one from Home.",
    signinPrompt: "Sign in to see your conversations",
    loadFailed: "Couldn't load. Try again.",
    backToList: "← Back",
    newCaseSubject: "Subject",
    newCaseBody: "Describe your issue…",
    create: "Create",
    cancel: "Cancel",
    aiIntro: "Hi! I'm the Navgar assistant — ask me anything and I'll help right away.",
    aiPlaceholder: "Ask a question…",
    aiError: "Something went wrong. Please try again.",
    signInToEscalate: "To connect you with a team member, enter your email and we'll create your support case.",
    caseCreated: "A support case was created — a team member will follow up.",
    caseLimitReached: "We couldn't open a new case — you've reached the limit of open cases. Please close or wait for an update on an existing case in Messages, then try again.",
    escalationFailed: "We couldn't create your support case right now. Please try again, or start a conversation from the Messages tab.",
    viewInMessages: "View in Messages",
    convoEmailCapture: "Enter your email and we'll send you a link — clicking it creates your support case and takes you straight to it.",
    convoLinkSent: "Check your email — clicking the link will create your support case and take you to it.",
    rateLimited: "Too many attempts — please try again in an hour.",
    newAiChat: "Start new conversation",
  },
  es: {
    greeting: "Hola {name} 👋",
    greetingAnon: "Hola 👋",
    subline: "¿En qué podemos ayudarte?",
    askQuestion: "Haz una pregunta",
    askSub: "El agente AI y el equipo pueden ayudar",
    searchPlaceholder: "Buscar ayuda",
    tabHome: "Inicio",
    tabMessages: "Mensajes",
    tabHelp: "Ayuda",
    newConvo: "Nueva conversación",
    emailCapture: "Ingresa tu correo para continuar",
    emailPlaceholder: "tu@ejemplo.com",
    namePlaceholder: "Tu nombre (opcional)",
    companyPlaceholder: "Empresa (opcional)",
    sendLink: "Enviarme un enlace",
    checkEmail: "Revisa tu correo — te enviamos un enlace de acceso.",
    composerPlaceholder: "Escribe tu mensaje…",
    send: "Enviar",
    noConvos: "Aún no tienes conversaciones. Empieza una desde Inicio.",
    signinPrompt: "Inicia sesión para ver tus conversaciones",
    loadFailed: "No se pudo cargar. Reintenta.",
    backToList: "← Atrás",
    newCaseSubject: "Asunto",
    newCaseBody: "Describe tu problema…",
    create: "Crear",
    cancel: "Cancelar",
    aiIntro: "¡Hola! Soy el asistente de Navgar — pregúntame lo que quieras y te ayudo enseguida.",
    aiPlaceholder: "Haz una pregunta…",
    aiError: "Algo salió mal. Inténtalo de nuevo.",
    signInToEscalate: "Para conectarte con un miembro del equipo, ingresa tu correo y crearemos tu caso de soporte.",
    caseCreated: "Se creó un caso de soporte — un miembro del equipo te contactará.",
    caseLimitReached: "No pudimos abrir un caso nuevo — alcanzaste el límite de casos abiertos. Cierra o espera una actualización de un caso existente en Mensajes e inténtalo de nuevo.",
    escalationFailed: "No pudimos crear tu caso de soporte en este momento. Inténtalo de nuevo o inicia una conversación desde la pestaña Mensajes.",
    viewInMessages: "Ver en Mensajes",
    convoEmailCapture: "Ingresa tu correo y te enviaremos un enlace — al hacer clic se creará tu caso de soporte y te llevará directo a él.",
    convoLinkSent: "Revisa tu correo — al hacer clic en el enlace se creará tu caso de soporte y te llevará a él.",
    rateLimited: "Demasiados intentos — inténtalo de nuevo en una hora.",
    newAiChat: "Iniciar conversación nueva",
  },
};

const STYLE = `
.nm-host * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
.nm-launcher { position: fixed; bottom: 20px; left: 20px; width: 56px; height: 56px; border-radius: 50%; background: #2563eb; color: white; border: none; box-shadow: 0 4px 16px rgba(0,0,0,0.18); cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 2147483600; transition: transform 0.15s; }
.nm-launcher:hover { transform: scale(1.05); }
.nm-launcher svg { width: 28px; height: 28px; }
.nm-env-tag { position: absolute; top: -9px; left: 50%; transform: translateX(-50%); background: #f59e0b; color: #111827; font-size: 9px; font-weight: 700; line-height: 1; padding: 3px 7px; border-radius: 9px; letter-spacing: 0.5px; white-space: nowrap; pointer-events: none; box-shadow: 0 1px 4px rgba(0,0,0,0.25); }
.nm-env-pill { display: inline-block; background: #f59e0b; color: #111827; font-size: 10px; font-weight: 700; line-height: 1; padding: 4px 9px; border-radius: 10px; letter-spacing: 0.5px; margin-left: 10px; vertical-align: middle; align-self: center; }
.nm-badge { position: absolute; top: -4px; right: -4px; min-width: 20px; height: 20px; border-radius: 10px; background: #ef4444; color: white; font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; padding: 0 6px; border: 2px solid white; }
.nm-panel { position: fixed; bottom: 90px; left: 20px; width: min(${PANEL_W}px, calc(100vw - 40px)); height: min(${PANEL_H}px, calc(100vh - 110px)); max-height: calc(100vh - 110px); background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.2); display: none; flex-direction: column; overflow: hidden; z-index: 2147483601; }
.nm-panel.nm-open { display: flex; animation: nm-slide 0.2s ease-out; }
@keyframes nm-slide { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.nm-header { background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%); color: white; padding: 20px 18px 28px; }
.nm-header-top { display: flex; justify-content: space-between; align-items: flex-start; }
.nm-avatar { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; overflow: hidden; }
.nm-avatar img { width: 100%; height: 100%; object-fit: cover; }
.nm-close { background: transparent; border: none; color: white; cursor: pointer; padding: 4px; opacity: 0.8; }
.nm-close:hover { opacity: 1; }
.nm-greeting { font-size: 24px; font-weight: 700; margin: 16px 0 4px; }
.nm-subline { font-size: 20px; font-weight: 500; opacity: 0.95; }
.nm-content { flex: 1; overflow-y: auto; padding: 0; background: #f9fafb; }
.nm-tabs { display: flex; border-top: 1px solid #e5e7eb; background: white; }
.nm-tab { flex: 1; padding: 10px 4px; text-align: center; cursor: pointer; border: none; background: transparent; color: #6b7280; font-size: 12px; font-weight: 500; }
.nm-tab.nm-active { color: #2563eb; }
.nm-tab-icon { display: block; margin: 0 auto 2px; width: 20px; height: 20px; }
.nm-card { background: white; border-radius: 12px; padding: 14px; margin: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); cursor: pointer; display: flex; justify-content: space-between; align-items: center; border: 1px solid #f3f4f6; }
.nm-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.nm-card-title { font-weight: 600; font-size: 14px; color: #111827; }
.nm-card-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
.nm-search { margin: 0 12px 12px; position: relative; }
.nm-search input { width: 100%; padding: 10px 36px 10px 12px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; outline: none; background: white; }
.nm-search input:focus { border-color: #2563eb; }
.nm-search-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.nm-msg-bubble a { color: #2563eb; text-decoration: underline; word-break: break-word; }
.nm-msg.nm-mine .nm-msg-bubble a { color: #ffffff; }
.nm-list-item { padding: 12px 16px; border-bottom: 1px solid #f3f4f6; cursor: pointer; background: white; }
.nm-list-item:hover { background: #f9fafb; }
.nm-list-item-title { font-weight: 600; font-size: 14px; color: #111827; }
.nm-list-item-sub { font-size: 12px; color: #6b7280; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nm-list-item-meta { font-size: 11px; color: #9ca3af; margin-top: 4px; }
.nm-unread-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #2563eb; margin-right: 6px; vertical-align: middle; }
.nm-empty { padding: 40px 20px; text-align: center; color: #6b7280; font-size: 14px; }
.nm-back { background: white; padding: 12px 16px; border-bottom: 1px solid #e5e7eb; }
.nm-back button { background: transparent; border: none; color: #2563eb; font-size: 14px; cursor: pointer; padding: 0; }
.nm-thread { padding: 12px 16px; }
.nm-msg { margin-bottom: 12px; display: flex; }
.nm-msg.nm-mine { justify-content: flex-end; }
.nm-msg > div { max-width: 80%; min-width: 0; }
.nm-msg-bubble { max-width: 100%; padding: 10px 14px; border-radius: 16px; font-size: 14px; line-height: 1.4; white-space: pre-wrap; word-wrap: break-word; }
.nm-msg.nm-mine .nm-msg-bubble { background: #2563eb; color: white; border-bottom-right-radius: 4px; }
.nm-msg:not(.nm-mine) .nm-msg-bubble { background: white; color: #111827; border: 1px solid #e5e7eb; border-bottom-left-radius: 4px; }
.nm-msg-meta { font-size: 10px; color: #9ca3af; margin-top: 4px; text-align: right; }
.nm-composer { display: flex; padding: 10px 12px; background: white; border-top: 1px solid #e5e7eb; gap: 8px; }
.nm-composer textarea { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 12px; font-size: 14px; resize: none; outline: none; min-height: 36px; max-height: 120px; font-family: inherit; }
.nm-composer textarea:focus { border-color: #2563eb; }
.nm-composer button { background: #2563eb; color: white; border: none; border-radius: 8px; padding: 0 16px; font-size: 14px; font-weight: 600; cursor: pointer; }
.nm-composer button:disabled { background: #9ca3af; cursor: not-allowed; }
.nm-form { padding: 16px; }
.nm-form input, .nm-form textarea { width: 100%; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; outline: none; margin-bottom: 10px; font-family: inherit; }
.nm-form input:focus, .nm-form textarea:focus { border-color: #2563eb; }
.nm-form button.nm-primary { background: #2563eb; color: white; border: none; border-radius: 8px; padding: 10px 16px; font-size: 14px; font-weight: 600; cursor: pointer; width: 100%; }
.nm-form button.nm-primary:disabled { background: #9ca3af; cursor: not-allowed; }
.nm-form button.nm-secondary { background: transparent; color: #6b7280; border: none; padding: 8px; font-size: 13px; cursor: pointer; width: 100%; margin-top: 6px; }
.nm-toast { background: #ecfdf5; color: #065f46; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin: 12px; }
.nm-error { background: #fef2f2; color: #991b1b; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin: 12px; }
.nm-escalate { margin: 4px 16px 12px; padding: 12px 14px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; }
.nm-escalate-text { font-size: 13px; color: #1e3a8a; margin-bottom: 8px; }
.nm-escalate input { width: 100%; padding: 9px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none; margin-bottom: 8px; font-family: inherit; }
.nm-escalate input:focus { border-color: #2563eb; }
.nm-escalate .nm-primary { background: #2563eb; color: white; border: none; border-radius: 8px; padding: 9px 14px; font-size: 14px; font-weight: 600; cursor: pointer; width: 100%; }
.nm-escalate .nm-primary:disabled { background: #9ca3af; cursor: not-allowed; }
.nm-escalate.nm-escalate-warn { background: #fffbeb; border-color: #fde68a; }
.nm-escalate.nm-escalate-warn .nm-escalate-text { color: #92400e; }
.nm-escalate.nm-escalate-warn .nm-primary { background: #d97706; }
.nm-article { padding: 16px; }
.nm-article h1 { font-size: 18px; font-weight: 700; color: #111827; margin: 0 0 12px; }
.nm-article-body { font-size: 14px; line-height: 1.6; color: #374151; overflow-wrap: break-word; word-break: break-word; max-width: 100%; overflow-x: hidden; }
.nm-article-body pre { overflow-x: auto; max-width: 100%; }
.nm-article-body table { display: block; overflow-x: auto; max-width: 100%; }
.nm-article-body p { margin: 0 0 12px; }
.nm-article-body h2 { font-size: 16px; font-weight: 600; margin: 16px 0 8px; }
.nm-article-body h3 { font-size: 14px; font-weight: 600; margin: 12px 0 6px; }
.nm-article-body a { color: #2563eb; }
.nm-article-body ul, .nm-article-body ol { padding-left: 20px; margin: 0 0 12px; }
.nm-article-body code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 13px; }
.nm-article-body img { max-width: 100%; height: auto; display: block; border-radius: 8px; margin: 8px 0; border: 1px solid #e5e7eb; }
.nm-article-body .nm-img-link { display: block; }
.nm-video-wrap { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 10px; margin: 12px 0; background: #111827; }
.nm-video-wrap iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; }
.nm-loading { padding: 40px; text-align: center; color: #9ca3af; font-size: 13px; }
.nm-typing { padding: 10px 14px; color: #6b7280; font-style: italic; font-size: 13px; }
@media (max-width: 480px) {
  /* Fullscreen on phones. 100dvh tracks the browser's dynamic toolbars and
     safe-area padding keeps the composer/tabs clear of the home indicator. */
  .nm-panel { width: 100%; height: 100vh; height: 100dvh; bottom: 0; left: 0; border-radius: 0; padding-bottom: env(safe-area-inset-bottom, 0px); }
  .nm-launcher { bottom: calc(16px + env(safe-area-inset-bottom, 0px)); left: 16px; }
}
`;

const ICONS = {
  bubble: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`,
  close: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  home: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  msg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  help: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
  search: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
};

class Messenger {
  private opts: BootOptions = {};
  private host: HTMLDivElement | null = null;
  private launcher: HTMLButtonElement | null = null;
  private panel: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private greeting: HTMLDivElement | null = null;
  private badge: HTMLDivElement | null = null;
  private currentTab: "home" | "messages" | "help" = "home";
  private me: MeResponse | null = null;
  private unreadPollTimer: number | null = null;
  private booted = false;
  private apiBase = "";
  private envLabel: string | null = null;
  private locale: "en" | "es" = "en";
  private aiHistory: Array<{ role: "user" | "assistant"; content: string }> = [];
  private aiSessionId: number | null = null;

  private t() {
    return COPY[this.locale];
  }

  private apiUrl(path: string): string {
    const base = this.apiBase.replace(/\/$/, "");
    return `${base}${path}`;
  }

  private async api(path: string, init?: RequestInit): Promise<Response> {
    return fetch(this.apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
      ...init,
    });
  }

  async boot(opts: BootOptions = {}): Promise<void> {
    if (this.booted) {
      // Re-boot: shutdown first.
      await this.shutdown();
    }
    this.opts = opts;
    this.apiBase = opts.apiBase || this.detectApiBase();
    linkBase = this.apiBase;
    const env = String(opts.environment ?? "").trim();
    this.envLabel = env && !/^prod(uction)?$/i.test(env) ? env.toUpperCase().slice(0, 20) : null;
    this.locale = opts.locale || (navigator.language?.startsWith("es") ? "es" : "en");
    this.injectStyles();
    this.mount();
    this.booted = true;

    // Try identified-mode handshake if user_hash provided.
    if (opts.user_id && opts.user_hash && opts.email) {
      try {
        const r = await this.api("/api/messenger/identify", {
          method: "POST",
          body: JSON.stringify({
            user_id: opts.user_id,
            user_hash: opts.user_hash,
            email: opts.email,
            email_hash: opts.email_hash,
            name: opts.name,
            company: opts.company,
            // Server may bind secrets to environments (env-labeled keys);
            // send the claimed environment so verification can enforce it.
            environment: env || undefined,
            // Auto-captured session context (Intercom-style). Best-effort;
            // used only for support context + campaign audience segmentation.
            context: {
              timezone: (() => {
                try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; }
              })(),
              browser_language: navigator.language || undefined,
              page_url: window.location.href,
              referrer: document.referrer || undefined,
            },
          }),
        });
        if (r.ok) {
          this.me = await this.api("/api/messenger/me").then((r) => r.json());
        } else if (r.status === 503) {
          // Identified mode not configured server-side; fall through to anon.
          console.warn("[NavgarMessenger] Identified mode not configured, falling back to anonymous");
        } else {
          // 401 = bad user_hash/environment mismatch, 409 = identity collision,
          // 400 = malformed boot payload. Surface loudly — the host page
          // supplied credentials, so silently showing the sign-in form here
          // makes this failure invisible.
          let detail: unknown = null;
          try {
            detail = await r.json();
          } catch {
            /* body may be empty */
          }
          console.error(
            `[NavgarMessenger] Identify rejected (HTTP ${r.status}) — falling back to anonymous mode. ` +
              "The user will see the sign-in form despite user_hash/email_hash being provided.",
            detail,
          );
        }
      } catch (err) {
        // fetch() throwing here usually means the browser blocked the request
        // (CORS: this page's origin is not on the API's credentialed
        // allowlist) or the network failed. The identify POST never reached
        // the server in that case.
        console.error(
          "[NavgarMessenger] Identify request failed before reaching the server — likely this page's origin " +
            "is not allowlisted for credentialed CORS on the API (MESSENGER_ALLOWED_ORIGINS). " +
            "Falling back to anonymous mode; the sign-in form will be shown.",
          err,
        );
      }
    }

    if (!this.me) {
      try {
        this.me = await this.api("/api/messenger/me").then((r) => r.json());
      } catch {
        this.me = { authenticated: false };
      }
    }

    this.renderHeader();
    this.startUnreadPoll();
    void this.loadArticleIds();
  }

  // Loads the set of valid KB article IDs so AI replies can't render clickable
  // links to articles that don't exist. Best-effort: on failure the set stays
  // null and links are allowed (fail open).
  private async loadArticleIds(): Promise<void> {
    try {
      const r = await this.api("/api/public/articles?limit=500");
      const data = await r.json();
      const arr = (Array.isArray(data) ? data : data.articles || data.results || []) as Array<{ id: number }>;
      knownArticleIds = new Set<number>(arr.map((a) => a.id).filter((n) => Number.isInteger(n)));
    } catch {
      /* leave null → fail open */
    }
  }

  async shutdown(): Promise<void> {
    if (this.unreadPollTimer) {
      clearInterval(this.unreadPollTimer);
      this.unreadPollTimer = null;
    }
    try {
      await this.api("/api/messenger/shutdown", { method: "POST" });
    } catch {
      /* best-effort */
    }
    this.host?.remove();
    this.host = this.launcher = this.panel = this.content = this.greeting = this.badge = null;
    this.me = null;
    this.aiHistory = [];
    this.aiSessionId = null;
    this.booted = false;
  }

  open(): void {
    this.panel?.classList.add("nm-open");
    this.renderTab(this.currentTab);
  }
  close(): void {
    this.panel?.classList.remove("nm-open");
  }

  private detectApiBase(): string {
    // Try to read from the script tag that loaded us.
    const scripts = document.querySelectorAll('script[src*="/embed/messenger"]');
    for (const s of Array.from(scripts) as HTMLScriptElement[]) {
      try {
        const u = new URL(s.src);
        return u.origin;
      } catch {
        /* skip */
      }
    }
    return window.location.origin;
  }

  private injectStyles(): void {
    if (document.getElementById("nm-styles")) return;
    const style = document.createElement("style");
    style.id = "nm-styles";
    style.textContent = STYLE;
    document.head.appendChild(style);
  }

  private mount(): void {
    document.getElementById(HOST_ID)?.remove();
    const host = document.createElement("div");
    host.id = HOST_ID;
    host.className = "nm-host";
    document.body.appendChild(host);
    this.host = host;

    if (!this.opts.hideLauncher) {
      const launcher = document.createElement("button");
      launcher.className = "nm-launcher";
      launcher.setAttribute("aria-label", "Open messenger");
      launcher.innerHTML = ICONS.bubble;
      launcher.onclick = () => {
        if (this.panel?.classList.contains("nm-open")) this.close();
        else this.open();
      };
      host.appendChild(launcher);
      this.launcher = launcher;

      if (this.envLabel) {
        const envTag = document.createElement("span");
        envTag.className = "nm-env-tag";
        envTag.textContent = this.envLabel;
        launcher.appendChild(envTag);
      }

      const badge = document.createElement("div");
      badge.className = "nm-badge";
      badge.style.display = "none";
      launcher.appendChild(badge);
      this.badge = badge;
    }

    const panel = document.createElement("div");
    panel.className = "nm-panel";

    const header = document.createElement("div");
    header.className = "nm-header";
    panel.appendChild(header);

    const headerTop = document.createElement("div");
    headerTop.className = "nm-header-top";
    const avatar = document.createElement("div");
    avatar.className = "nm-avatar";
    avatar.textContent = "N";
    const close = document.createElement("button");
    close.className = "nm-close";
    close.innerHTML = ICONS.close;
    close.onclick = () => this.close();
    headerTop.appendChild(avatar);
    if (this.envLabel) {
      const envPill = document.createElement("span");
      envPill.className = "nm-env-pill";
      envPill.textContent = this.envLabel;
      const left = document.createElement("div");
      left.style.display = "flex";
      left.style.alignItems = "center";
      headerTop.insertBefore(left, avatar);
      left.appendChild(avatar);
      left.appendChild(envPill);
    }
    headerTop.appendChild(close);
    header.appendChild(headerTop);

    const greeting = document.createElement("div");
    greeting.className = "nm-greeting";
    greeting.textContent = this.t().greetingAnon;
    header.appendChild(greeting);
    this.greeting = greeting;

    const subline = document.createElement("div");
    subline.className = "nm-subline";
    subline.textContent = this.t().subline;
    header.appendChild(subline);

    const content = document.createElement("div");
    content.className = "nm-content";
    panel.appendChild(content);
    this.content = content;

    const tabs = document.createElement("div");
    tabs.className = "nm-tabs";
    panel.appendChild(tabs);

    const tabDefs: Array<{ id: "home" | "messages" | "help"; label: string; icon: string }> = [
      { id: "home", label: this.t().tabHome, icon: ICONS.home },
      { id: "messages", label: this.t().tabMessages, icon: ICONS.msg },
      { id: "help", label: this.t().tabHelp, icon: ICONS.help },
    ];
    for (const def of tabDefs) {
      const btn = document.createElement("button");
      btn.className = "nm-tab" + (def.id === this.currentTab ? " nm-active" : "");
      btn.dataset.tab = def.id;
      btn.innerHTML = `<span class="nm-tab-icon">${def.icon}</span>${def.label}`;
      btn.onclick = () => this.selectTab(def.id);
      tabs.appendChild(btn);
    }

    host.appendChild(panel);
    this.panel = panel;
  }

  private renderHeader(): void {
    if (!this.greeting) return;
    const name = this.me?.user?.name?.split(" ")[0] || this.opts.name?.split(" ")[0];
    this.greeting.textContent = name ? this.t().greeting.replace("{name}", name) : this.t().greetingAnon;
  }

  private selectTab(id: typeof this.currentTab): void {
    this.currentTab = id;
    this.panel?.querySelectorAll(".nm-tab").forEach((el) => {
      (el as HTMLElement).classList.toggle("nm-active", (el as HTMLElement).dataset.tab === id);
    });
    this.renderTab(id);
  }

  private renderTab(id: typeof this.currentTab): void {
    if (!this.content) return;
    this.content.innerHTML = "";
    if (id === "home") this.renderHome();
    else if (id === "messages") this.renderMessages();
    else if (id === "help") this.renderHelp();
  }

  // ---------------------------- HOME ----------------------------
  private renderHome(): void {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = "";

    const ask = document.createElement("div");
    ask.className = "nm-card";
    ask.innerHTML = `<div><div class="nm-card-title">${t.askQuestion}</div><div class="nm-card-sub">${t.askSub}</div></div>`;
    ask.onclick = () => this.openAskFlow();
    this.content.appendChild(ask);

    const search = document.createElement("div");
    search.className = "nm-search";
    search.innerHTML = `<input type="text" placeholder="${t.searchPlaceholder}" /><span class="nm-search-icon">${ICONS.search}</span>`;
    const input = search.querySelector("input") as HTMLInputElement;
    let debounce: number | null = null;
    input.addEventListener("input", () => {
      if (debounce) clearTimeout(debounce);
      const q = input.value.trim();
      debounce = window.setTimeout(() => this.runSearch(q), 250);
    });
    this.content.appendChild(search);

    const results = document.createElement("div");
    results.id = "nm-search-results";
    this.content.appendChild(results);
  }

  private async runSearch(q: string): Promise<void> {
    const target = this.content?.querySelector("#nm-search-results") as HTMLDivElement | null;
    if (!target) return;
    if (!q) {
      target.innerHTML = "";
      return;
    }
    target.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api(`/api/public/search?q=${encodeURIComponent(q)}&limit=8`);
      const data = await r.json();
      const articles = (Array.isArray(data) ? data : data.articles || data.results || []) as Array<{ id: number; title: string; summary?: string; snippet?: string }>;
      if (!articles.length) {
        target.innerHTML = `<div class="nm-empty">No results.</div>`;
        return;
      }
      target.innerHTML = "";
      for (const a of articles) {
        const item = document.createElement("div");
        item.className = "nm-list-item";
        const sub = a.summary ?? a.snippet;
        item.innerHTML = `<div class="nm-list-item-title">${escape(a.title)}</div>${sub ? `<div class="nm-list-item-sub">${escape(sub)}</div>` : ""}`;
        item.onclick = () => this.openArticle(a.id);
        target.appendChild(item);
      }
    } catch {
      target.innerHTML = `<div class="nm-error">${this.t().loadFailed}</div>`;
    }
  }

  private openAskFlow(): void {
    this.renderAskAI();
  }

  private renderAskAI(): void {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = "";

    const back = document.createElement("div");
    back.className = "nm-back";
    back.innerHTML = `<button>${t.backToList}</button>`;
    (back.querySelector("button") as HTMLButtonElement).onclick = () => this.selectTab("home");
    if (this.aiHistory.length) {
      // Deterministic reset: clears the session so a fresh escalation can
      // open a NEW case (escalated sessions are otherwise reused server-side
      // to keep one conversation = one case).
      const reset = document.createElement("button");
      reset.textContent = t.newAiChat;
      reset.onclick = () => {
        this.aiHistory = [];
        this.aiSessionId = null;
        this.renderAskAI();
      };
      back.appendChild(reset);
    }
    this.content.appendChild(back);

    const thread = document.createElement("div");
    thread.className = "nm-thread";
    this.content.appendChild(thread);

    if (!this.aiHistory.length) {
      const intro = document.createElement("div");
      intro.className = "nm-msg";
      intro.innerHTML = `<div><div class="nm-msg-bubble">${escape(t.aiIntro)}</div></div>`;
      thread.appendChild(intro);
    } else {
      for (const m of this.aiHistory) {
        const row = document.createElement("div");
        row.className = "nm-msg" + (m.role === "user" ? " nm-mine" : "");
        const body = m.role === "user" ? escape(m.content) : renderRich(m.content, true);
        row.innerHTML = `<div><div class="nm-msg-bubble">${body}</div></div>`;
        thread.appendChild(row);
      }
    }
    thread.scrollTop = thread.scrollHeight;

    const composer = document.createElement("div");
    composer.className = "nm-composer";
    composer.innerHTML = `<textarea placeholder="${t.aiPlaceholder}"></textarea><button>${t.send}</button>`;
    const ta = composer.querySelector("textarea") as HTMLTextAreaElement;
    const btn = composer.querySelector("button") as HTMLButtonElement;

    const send = async (): Promise<void> => {
      const text = ta.value.trim();
      if (!text) return;
      ta.value = "";
      btn.disabled = true;
      ta.disabled = true;

      this.aiHistory.push({ role: "user", content: text });
      const userRow = document.createElement("div");
      userRow.className = "nm-msg nm-mine";
      userRow.innerHTML = `<div><div class="nm-msg-bubble">${escape(text)}</div></div>`;
      thread.appendChild(userRow);

      const botRow = document.createElement("div");
      botRow.className = "nm-msg";
      const bubble = document.createElement("div");
      bubble.className = "nm-msg-bubble";
      bubble.textContent = "…";
      const botInner = document.createElement("div");
      botInner.appendChild(bubble);
      botRow.appendChild(botInner);
      thread.appendChild(botRow);
      thread.scrollTop = thread.scrollHeight;

      let acc = "";
      let final: { escalated?: boolean; needSignIn?: boolean; caseId?: number | null; sessionId?: number | null; escalationLimitReached?: boolean; escalationFailed?: boolean } = {};
      try {
        const r = await this.api("/api/public/chat", {
          method: "POST",
          body: JSON.stringify({
            message: text,
            conversationHistory: this.aiHistory.slice(0, -1),
            sessionId: this.aiSessionId ?? undefined,
            language: this.locale,
          }),
        });
        if (!r.ok || !r.body) throw new Error("bad response");
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const handleEvent = (raw: string): void => {
          const payloads = raw
            .split(/\r?\n/)
            .filter((l) => l.startsWith("data:"))
            .map((l) => l.slice(5).trim())
            .filter(Boolean);
          if (!payloads.length) return;
          const payload = payloads.join("");
          let evt: { content?: string; done?: boolean; error?: string; escalated?: boolean; needSignIn?: boolean; caseId?: number | null; sessionId?: number | null; escalationLimitReached?: boolean; escalationFailed?: boolean } | null = null;
          try { evt = JSON.parse(payload); } catch { return; }
          if (!evt) return;
          if (evt.error) throw new Error(evt.error);
          if (typeof evt.content === "string") {
            acc += evt.content;
            bubble.textContent = acc.replace(/\s*\[ESCALATE\]\s*$/i, "");
            thread.scrollTop = thread.scrollHeight;
          }
          if (evt.done) final = evt;
        };
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split(/\r?\n\r?\n/);
          buffer = parts.pop() || "";
          for (const part of parts) handleEvent(part);
        }
        if (buffer.trim()) handleEvent(buffer);
        const clean = acc.replace(/\s*\[ESCALATE\]\s*$/i, "").trim();
        if (clean) {
          bubble.innerHTML = renderRich(clean, true);
          this.aiHistory.push({ role: "assistant", content: clean });
        } else {
          bubble.textContent = t.aiError;
        }
        if (typeof final.sessionId === "number") this.aiSessionId = final.sessionId;

        if (final.escalationLimitReached) {
          this.renderEscalationNotice(thread, t.caseLimitReached, true);
        } else if (final.escalationFailed) {
          this.renderEscalationNotice(thread, t.escalationFailed, false);
        } else if (final.escalated) {
          if (final.needSignIn && !this.me?.authenticated) {
            this.renderEscalationSignIn(thread);
          } else if (final.caseId) {
            this.renderCaseCreatedNotice(thread);
          }
        }
      } catch {
        bubble.textContent = t.aiError;
      } finally {
        btn.disabled = false;
        ta.disabled = false;
        ta.focus();
        thread.scrollTop = thread.scrollHeight;
      }
    };

    btn.onclick = () => void send();
    ta.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void send();
      }
    });
    this.content.appendChild(composer);
    ta.focus();
  }

  private renderEscalationSignIn(thread: HTMLDivElement): void {
    const t = this.t();
    const box = document.createElement("div");
    box.className = "nm-escalate";
    box.innerHTML = `
      <div class="nm-escalate-text">${escape(t.signInToEscalate)}</div>
      <input type="email" id="nm-esc-email" placeholder="${t.emailPlaceholder}" required />
      <input type="text" id="nm-esc-name" placeholder="${t.namePlaceholder}" />
      <button class="nm-primary" id="nm-esc-submit">${t.sendLink}</button>
      <div id="nm-esc-msg"></div>
    `;
    thread.appendChild(box);
    const submit = box.querySelector("#nm-esc-submit") as HTMLButtonElement;
    const msg = box.querySelector("#nm-esc-msg") as HTMLDivElement;
    submit.onclick = async () => {
      const email = (box.querySelector("#nm-esc-email") as HTMLInputElement).value.trim();
      const name = (box.querySelector("#nm-esc-name") as HTMLInputElement).value.trim();
      if (!email) return;
      submit.disabled = true;
      submit.textContent = "…";
      try {
        const r = await this.api("/api/auth/magic-link/request", {
          method: "POST",
          body: JSON.stringify({
            email,
            name: name || undefined,
            sourceUrl: window.location.href,
            escalate: true,
            chatHistory: this.aiHistory,
            locale: this.locale,
          }),
        });
        msg.innerHTML = r.ok
          ? `<div class="nm-toast">${t.checkEmail}</div>`
          : `<div class="nm-error">${r.status === 429 ? t.rateLimited : t.loadFailed}</div>`;
        if (!r.ok) { submit.disabled = false; submit.textContent = t.sendLink; }
      } catch {
        msg.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
        submit.disabled = false;
        submit.textContent = t.sendLink;
      }
    };
    thread.scrollTop = thread.scrollHeight;
  }

  private renderEscalationNotice(thread: HTMLDivElement, text: string, showMessagesButton: boolean): void {
    const t = this.t();
    const box = document.createElement("div");
    box.className = "nm-escalate nm-escalate-warn";
    box.innerHTML = `<div class="nm-escalate-text">${escape(text)}</div>`;
    if (showMessagesButton) {
      const btn = document.createElement("button");
      btn.className = "nm-primary";
      btn.textContent = t.viewInMessages;
      btn.onclick = () => this.selectTab("messages");
      box.appendChild(btn);
    }
    thread.appendChild(box);
    thread.scrollTop = thread.scrollHeight;
  }

  private renderCaseCreatedNotice(thread: HTMLDivElement): void {
    const t = this.t();
    const box = document.createElement("div");
    box.className = "nm-escalate";
    box.innerHTML = `<div class="nm-escalate-text">${escape(t.caseCreated)}</div>`;
    const btn = document.createElement("button");
    btn.className = "nm-primary";
    btn.textContent = t.viewInMessages;
    btn.onclick = () => this.selectTab("messages");
    box.appendChild(btn);
    thread.appendChild(box);
    thread.scrollTop = thread.scrollHeight;
  }

  // NOTE: appends to the current content (does not clear) — callers are
  // responsible for resetting the panel before invoking.
  private renderEmailCapture(onSuccess: () => void): void {
    if (!this.content) return;
    const t = this.t();
    const wrap = document.createElement("div");
    wrap.className = "nm-form";
    wrap.innerHTML = `
      <div style="font-size:14px;color:#374151;margin-bottom:12px;">${t.emailCapture}</div>
      <input type="email" id="nm-email" placeholder="${t.emailPlaceholder}" required />
      <input type="text" id="nm-name" placeholder="${t.namePlaceholder}" />
      <input type="text" id="nm-company" placeholder="${t.companyPlaceholder}" />
      <button class="nm-primary" id="nm-submit">${t.sendLink}</button>
      <div id="nm-form-msg"></div>
    `;
    this.content.appendChild(wrap);
    const submit = wrap.querySelector("#nm-submit") as HTMLButtonElement;
    const msg = wrap.querySelector("#nm-form-msg") as HTMLDivElement;
    submit.onclick = async () => {
      const email = (wrap.querySelector("#nm-email") as HTMLInputElement).value.trim();
      const name = (wrap.querySelector("#nm-name") as HTMLInputElement).value.trim();
      const company = (wrap.querySelector("#nm-company") as HTMLInputElement).value.trim();
      if (!email) return;
      submit.disabled = true;
      submit.textContent = "…";
      try {
        const r = await this.api("/api/auth/magic-link/request", {
          method: "POST",
          body: JSON.stringify({
            email,
            name: name || undefined,
            company: company || undefined,
            sourceUrl: window.location.href,
          }),
        });
        if (r.ok) {
          msg.innerHTML = `<div class="nm-toast">${t.checkEmail}</div>`;
        } else {
          msg.innerHTML = `<div class="nm-error">${r.status === 429 ? t.rateLimited : t.loadFailed}</div>`;
          submit.disabled = false;
          submit.textContent = t.sendLink;
        }
      } catch {
        msg.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
        submit.disabled = false;
        submit.textContent = t.sendLink;
      }
    };
    // Note: onSuccess unused for now — magic-link verify happens in a new tab,
    // user will reopen widget; could add polling for session here later.
    void onSuccess;
  }

  // ---------------------------- MESSAGES ----------------------------
  private async renderMessages(): Promise<void> {
    if (!this.content) return;
    const t = this.t();
    if (!this.me?.authenticated) {
      this.content.innerHTML = "";
      const newBtn = document.createElement("div");
      newBtn.className = "nm-card";
      newBtn.innerHTML = `<div class="nm-card-title">+ ${t.newConvo}</div>`;
      newBtn.onclick = () => this.renderNewConvo();
      this.content.appendChild(newBtn);
      const prompt = document.createElement("div");
      prompt.className = "nm-empty";
      prompt.textContent = t.signinPrompt;
      this.content.appendChild(prompt);
      this.renderEmailCapture(() => this.renderMessages());
      return;
    }
    this.content.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api("/api/messenger/conversations");
      const data = await r.json();
      const convos = (data.conversations || []) as ConversationSummary[];
      this.content.innerHTML = "";
      const newBtn = document.createElement("div");
      newBtn.className = "nm-card";
      newBtn.innerHTML = `<div class="nm-card-title">+ ${t.newConvo}</div>`;
      newBtn.onclick = () => this.renderNewConvo();
      this.content.appendChild(newBtn);
      if (!convos.length) {
        const empty = document.createElement("div");
        empty.className = "nm-empty";
        empty.textContent = t.noConvos;
        this.content.appendChild(empty);
        return;
      }
      for (const c of convos) {
        const item = document.createElement("div");
        item.className = "nm-list-item";
        item.innerHTML = `
          <div class="nm-list-item-title">${c.unread ? '<span class="nm-unread-dot"></span>' : ""}${escape(c.subject)}</div>
          <div class="nm-list-item-sub">${escape(c.preview)}</div>
          <div class="nm-list-item-meta">${formatTime(c.updatedAt)} · ${c.status}</div>
        `;
        item.onclick = () => this.openConvo(c.id);
        this.content.appendChild(item);
      }
    } catch {
      this.content.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
    }
  }

  private async openConvo(id: number): Promise<void> {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api(`/api/messenger/conversations/${id}`);
      const data = await r.json();
      const messages = (data.messages || []) as Message[];
      // Mark read via POST (server keeps GET side-effect free); best-effort.
      void this.api(`/api/messenger/conversations/${id}/read`, { method: "POST" }).catch(() => {});
      this.content.innerHTML = "";
      const back = document.createElement("div");
      back.className = "nm-back";
      back.innerHTML = `<button>${t.backToList}</button>`;
      (back.querySelector("button") as HTMLButtonElement).onclick = () => this.renderMessages();
      this.content.appendChild(back);

      const thread = document.createElement("div");
      thread.className = "nm-thread";
      this.content.appendChild(thread);
      for (const m of messages) {
        const row = document.createElement("div");
        row.className = "nm-msg" + (m.isMine ? " nm-mine" : "");
        row.innerHTML = `<div><div class="nm-msg-bubble">${escape(m.content)}</div><div class="nm-msg-meta">${formatTime(m.createdAt)}</div></div>`;
        thread.appendChild(row);
      }
      thread.scrollTop = thread.scrollHeight;

      const composer = document.createElement("div");
      composer.className = "nm-composer";
      composer.innerHTML = `<textarea placeholder="${t.composerPlaceholder}"></textarea><button>${t.send}</button>`;
      const ta = composer.querySelector("textarea") as HTMLTextAreaElement;
      const btn = composer.querySelector("button") as HTMLButtonElement;
      const send = async () => {
        const body = ta.value.trim();
        if (!body) return;
        btn.disabled = true;
        ta.disabled = true;
        try {
          const r = await this.api(`/api/messenger/conversations/${id}/messages`, {
            method: "POST",
            body: JSON.stringify({ body }),
          });
          if (r.ok) {
            ta.value = "";
            await this.openConvo(id);
          } else {
            let err = composer.querySelector(".nm-error") as HTMLDivElement | null;
            if (!err) {
              err = document.createElement("div");
              err.className = "nm-error";
              composer.appendChild(err);
            }
            err.textContent = r.status === 429 ? t.rateLimited : t.loadFailed;
          }
        } finally {
          btn.disabled = false;
          ta.disabled = false;
        }
      };
      btn.onclick = send;
      ta.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          void send();
        }
      });
      this.content.appendChild(composer);
      ta.focus();
    } catch {
      this.content.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
    }
  }

  private renderNewConvo(): void {
    if (!this.content) return;
    if (!this.me?.authenticated) {
      this.renderAnonNewConvo();
      return;
    }
    const t = this.t();
    this.content.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "nm-form";
    wrap.innerHTML = `
      <input type="text" id="nm-subj" placeholder="${t.newCaseSubject}" />
      <textarea id="nm-body" placeholder="${t.newCaseBody}" rows="6"></textarea>
      <button class="nm-primary" id="nm-create">${t.create}</button>
      <button class="nm-secondary" id="nm-cancel">${t.cancel}</button>
    `;
    this.content.appendChild(wrap);
    (wrap.querySelector("#nm-cancel") as HTMLButtonElement).onclick = () => this.renderMessages();
    (wrap.querySelector("#nm-create") as HTMLButtonElement).onclick = async () => {
      const subject = (wrap.querySelector("#nm-subj") as HTMLInputElement).value.trim();
      const body = (wrap.querySelector("#nm-body") as HTMLTextAreaElement).value.trim();
      if (!subject || !body) return;
      const btn = wrap.querySelector("#nm-create") as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await this.api("/api/messenger/conversations", {
          method: "POST",
          body: JSON.stringify({
            subject: this.envLabel ? `[${this.envLabel}] ${subject}` : subject,
            body,
            sourceUrl: window.location.href,
          }),
        });
        if (r.ok) {
          const data = await r.json();
          await this.openConvo(data.id);
        } else {
          btn.disabled = false;
          let err = wrap.querySelector(".nm-error") as HTMLDivElement | null;
          if (!err) {
            err = document.createElement("div");
            err.className = "nm-error";
            wrap.appendChild(err);
          }
          err.textContent =
            r.status === 409
              ? t.caseLimitReached
              : r.status === 429
                ? t.rateLimited
                : t.loadFailed;
        }
      } catch {
        btn.disabled = false;
      }
    };
  }

  // Anonymous new-conversation: compose the request first, then capture the
  // email. The magic-link email carries the pending conversation — clicking
  // the link creates the case server-side and lands on /cases/{id}.
  private renderAnonNewConvo(): void {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "nm-form";
    wrap.innerHTML = `
      <input type="text" id="nm-subj" placeholder="${t.newCaseSubject}" />
      <textarea id="nm-body" placeholder="${t.newCaseBody}" rows="5"></textarea>
      <div style="font-size:13px;color:#374151;margin:4px 0 8px;">${t.convoEmailCapture}</div>
      <input type="email" id="nm-email" placeholder="${t.emailPlaceholder}" required />
      <input type="text" id="nm-name" placeholder="${t.namePlaceholder}" />
      <input type="text" id="nm-company" placeholder="${t.companyPlaceholder}" />
      <button class="nm-primary" id="nm-create">${t.sendLink}</button>
      <button class="nm-secondary" id="nm-cancel">${t.cancel}</button>
      <div id="nm-form-msg"></div>
    `;
    this.content.appendChild(wrap);
    (wrap.querySelector("#nm-cancel") as HTMLButtonElement).onclick = () => this.renderMessages();
    const btn = wrap.querySelector("#nm-create") as HTMLButtonElement;
    const msg = wrap.querySelector("#nm-form-msg") as HTMLDivElement;
    btn.onclick = async () => {
      const subject = (wrap.querySelector("#nm-subj") as HTMLInputElement).value.trim();
      const body = (wrap.querySelector("#nm-body") as HTMLTextAreaElement).value.trim();
      const email = (wrap.querySelector("#nm-email") as HTMLInputElement).value.trim();
      const name = (wrap.querySelector("#nm-name") as HTMLInputElement).value.trim();
      const company = (wrap.querySelector("#nm-company") as HTMLInputElement).value.trim();
      if (!subject || !body || !email) return;
      btn.disabled = true;
      btn.textContent = "…";
      try {
        const r = await this.api("/api/auth/magic-link/request", {
          method: "POST",
          body: JSON.stringify({
            email,
            name: name || undefined,
            company: company || undefined,
            sourceUrl: window.location.href,
            locale: this.locale,
            pendingConversation: {
              subject: this.envLabel ? `[${this.envLabel}] ${subject}` : subject,
              body,
            },
          }),
        });
        if (r.ok) {
          msg.innerHTML = `<div class="nm-toast">${t.convoLinkSent}</div>`;
        } else {
          msg.innerHTML = `<div class="nm-error">${r.status === 429 ? t.rateLimited : t.loadFailed}</div>`;
          btn.disabled = false;
          btn.textContent = t.sendLink;
        }
      } catch {
        msg.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
        btn.disabled = false;
        btn.textContent = t.sendLink;
      }
    };
  }

  // ---------------------------- HELP ----------------------------
  private async renderHelp(): Promise<void> {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api("/api/public/categories");
      const data = await r.json();
      const cats = (data.categories || data) as Array<{ id: number; name: string; description?: string }>;
      this.content.innerHTML = "";
      if (!cats.length) {
        this.content.innerHTML = `<div class="nm-empty">No help articles yet.</div>`;
        return;
      }
      for (const c of cats) {
        const item = document.createElement("div");
        item.className = "nm-list-item";
        item.innerHTML = `<div class="nm-list-item-title">${escape(c.name)}</div>${c.description ? `<div class="nm-list-item-sub">${escape(c.description)}</div>` : ""}`;
        item.onclick = () => this.openCategory(c.id, c.name);
        this.content.appendChild(item);
      }
    } catch {
      this.content.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
    }
  }

  private async openCategory(id: number, name: string): Promise<void> {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api(`/api/public/articles?categoryId=${id}&limit=50`);
      const data = await r.json();
      const articles = (Array.isArray(data) ? data : data.articles || []) as Array<{ id: number; title: string; summary?: string }>;
      this.content.innerHTML = "";
      const back = document.createElement("div");
      back.className = "nm-back";
      back.innerHTML = `<button>${t.backToList}</button>`;
      (back.querySelector("button") as HTMLButtonElement).onclick = () => this.renderHelp();
      this.content.appendChild(back);
      const header = document.createElement("div");
      header.style.cssText = "padding:12px 16px;font-weight:600;color:#111827;";
      header.textContent = name;
      this.content.appendChild(header);
      if (!articles.length) {
        this.content.appendChild(Object.assign(document.createElement("div"), { className: "nm-empty", textContent: "No articles." }));
        return;
      }
      for (const a of articles) {
        const item = document.createElement("div");
        item.className = "nm-list-item";
        const sub = a.summary;
        item.innerHTML = `<div class="nm-list-item-title">${escape(a.title)}</div>${sub ? `<div class="nm-list-item-sub">${escape(sub)}</div>` : ""}`;
        item.onclick = () => this.openArticle(a.id);
        this.content!.appendChild(item);
      }
    } catch {
      this.content.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
    }
  }

  private async openArticle(id: number): Promise<void> {
    if (!this.content) return;
    const t = this.t();
    this.content.innerHTML = `<div class="nm-loading">…</div>`;
    try {
      const r = await this.api(`/api/public/articles/${id}`);
      const data = await r.json();
      const a = (data.article || data) as { title: string; contentHtml?: string; content?: string };
      this.content.innerHTML = "";
      const back = document.createElement("div");
      back.className = "nm-back";
      back.innerHTML = `<button>${t.backToList}</button>`;
      (back.querySelector("button") as HTMLButtonElement).onclick = () => this.selectTab("help");
      this.content.appendChild(back);
      const wrap = document.createElement("div");
      wrap.className = "nm-article";
      const body = a.contentHtml || a.content || "";
      // Article bodies are authored as Markdown (the help-center renders them
      // via react-markdown). A few legacy articles store raw HTML instead.
      // Detect legacy HTML by the presence of closing block-level tags or a
      // leading block tag — signals that authored Markdown virtually never
      // contains — and skip Markdown conversion for those. Everything still
      // flows through sanitizeHtml() either way.
      const looksHtml =
        /<\/(?:p|h[1-6]|ul|ol|li|div|table|thead|tbody|tr|td|th|blockquote|pre)>/i.test(body) ||
        /^\s*<(?:p|h[1-6]|ul|ol|div|table|blockquote|pre)\b/i.test(body);
      const rendered = looksHtml ? body : markdownToHtml(body);
      wrap.innerHTML = `<h1>${escape(a.title)}</h1><div class="nm-article-body">${sanitizeHtml(rendered)}</div>`;
      const articleBody = wrap.querySelector(".nm-article-body") as HTMLElement;
      // Legacy raw-HTML articles may have headings without ids — backfill
      // them with the same slugs the help-center site generates, so authored
      // `#anchor` links resolve. Existing ids are kept and count toward
      // dedupe so we never produce a duplicate.
      const slug = makeSlugger();
      articleBody.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((el) => {
        const existing = el.getAttribute("id");
        if (existing) { slug(existing); return; }
        const id = slug((el.textContent || "").trim());
        if (id) el.setAttribute("id", id);
      });
      // Intercept in-article anchor clicks: scroll the widget's article view
      // to the target heading instead of letting the browser navigate the
      // host page. Unresolvable anchors are a no-op.
      articleBody.addEventListener("click", (e) => {
        const link = (e.target as Element | null)?.closest?.("a");
        if (!link || !articleBody.contains(link)) return;
        const href = link.getAttribute("href") || "";
        // Treat absolute links carrying a fragment as in-article anchors too —
        // legacy imported articles often link the full old help-site URL.
        let fragment = "";
        if (href.startsWith("#")) {
          fragment = href.slice(1);
        } else if (href.includes("#")) {
          fragment = href.slice(href.indexOf("#") + 1);
        }
        if (!fragment) return;
        let id = "";
        try { id = decodeURIComponent(fragment); } catch { id = fragment; }
        if (!id) return;
        let target: Element | null = null;
        try { target = articleBody.querySelector(`[id="${CSS.escape(id)}"]`); } catch { /* invalid selector */ }
        // Legacy Intercom anchors (#h_abc123) don't exist in migrated content —
        // the heading ids were regenerated from heading text. TOC entries use
        // the heading's text as the link label, so fall back to slug(label).
        if (!target) {
          const bySlug = slugify((link.textContent || "").trim());
          if (bySlug) {
            try { target = articleBody.querySelector(`[id="${CSS.escape(bySlug)}"]`); } catch { /* ignore */ }
          }
        }
        // Only hijack navigation when we can resolve the target in-article;
        // otherwise leave external links (with fragments) working normally.
        if (!target && !href.startsWith("#")) return;
        e.preventDefault();
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      this.content.appendChild(wrap);
    } catch {
      this.content.innerHTML = `<div class="nm-error">${t.loadFailed}</div>`;
    }
  }

  // ---------------------------- UNREAD POLL ----------------------------
  private startUnreadPoll(): void {
    if (this.unreadPollTimer) clearInterval(this.unreadPollTimer);
    const tick = async () => {
      if (!this.me?.authenticated) return;
      try {
        const r = await this.api("/api/messenger/unread");
        const data = await r.json();
        this.setUnread(data.count || 0);
      } catch {
        /* silent */
      }
    };
    void tick();
    this.unreadPollTimer = window.setInterval(tick, 30_000);
  }

  private setUnread(n: number): void {
    if (!this.badge) return;
    if (n > 0) {
      this.badge.textContent = n > 9 ? "9+" : String(n);
      this.badge.style.display = "flex";
    } else {
      this.badge.style.display = "none";
    }
  }
}

function escape(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

// Set of valid KB article IDs, loaded at boot. Used to neutralise links the
// AI may hallucinate to non-existent help-center articles. null = not loaded
// yet, in which case we fail open (allow the link), matching the help-center
// web widget's behaviour.
let knownArticleIds: Set<number> | null = null;

// The messenger's API origin (e.g. https://knowledge.navgar.ai). Used to turn
// root-relative help-center links into absolute URLs so they open on the right
// domain instead of the marketing page hosting the widget (where they'd 404).
let linkBase = "";

// Resolve a root-relative URL (e.g. /help-center/article/7) against linkBase.
// Leaves absolute, protocol-relative, mailto and tel URLs untouched.
function absUrl(url: string): string {
  if (/^\/(?!\/)/.test(url)) return `${linkBase.replace(/\/$/, "")}${url}`;
  return url;
}

// Extract a help-center article id from a URL (absolute or root-relative),
// e.g. https://help.navgar.com/help-center/article/42 or /help-center/article/42.
function articleIdFromUrl(url: string): number | null {
  const m = /\/help-center\/article\/(\d+)/.exec(url);
  return m ? parseInt(m[1], 10) : null;
}

// True only when the URL points to a help-center article we KNOW is missing
// (valid-id set loaded and id absent). Unknown/non-article URLs → false.
function isDeadArticleLink(url: string): boolean {
  const id = articleIdFromUrl(url);
  if (id == null) return false;
  return knownArticleIds != null && !knownArticleIds.has(id);
}

// Map a Loom/YouTube/Vimeo page URL to its canonical embed-player URL.
// Returns null for anything else — only these three players may ever be
// embedded in the widget (mirrors the help-center site's getVideoEmbedUrl).
function videoEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    if (parsed.protocol !== "https:") return null;
    if (parsed.hostname === "loom.com" || parsed.hostname === "www.loom.com") {
      const m = /^\/(?:share|embed)\/([A-Za-z0-9_-]+)/.exec(parsed.pathname);
      if (m) return `https://www.loom.com/embed/${m[1]}`;
    }
    if (parsed.hostname === "www.youtube.com" || parsed.hostname === "youtube.com") {
      const v = parsed.searchParams.get("v");
      if (v && /^[A-Za-z0-9_-]+$/.test(v)) return `https://www.youtube.com/embed/${v}`;
      const e = /^\/embed\/([A-Za-z0-9_-]+)/.exec(parsed.pathname);
      if (e) return `https://www.youtube.com/embed/${e[1]}`;
    }
    if (parsed.hostname === "player.vimeo.com") {
      const m = /^\/video\/(\d+)/.exec(parsed.pathname);
      if (m) return `https://player.vimeo.com/video/${m[1]}`;
    }
    if (parsed.hostname === "youtu.be") {
      const id = parsed.pathname.slice(1);
      if (id && /^[A-Za-z0-9_-]+$/.test(id)) return `https://www.youtube.com/embed/${id}`;
    }
    if (parsed.hostname === "vimeo.com" || parsed.hostname === "www.vimeo.com") {
      const m = /^\/(\d+)/.exec(parsed.pathname);
      if (m) return `https://player.vimeo.com/video/${m[1]}`;
    }
    return null;
  } catch {
    return null;
  }
}

// Responsive 16:9 embedded player. embedUrl MUST come from videoEmbedUrl().
function videoIframeHtml(embedUrl: string): string {
  return `<div class="nm-video-wrap"><iframe src="${embedUrl}" loading="lazy" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" title="Video"></iframe></div>`;
}

// Per-article set of already-embedded player URLs. Loom transcripts link the
// SAME video many times (once per timestamp step) — only the first mention
// becomes an embedded player; later mentions stay ordinary links so the
// article isn't a wall of identical players. Reset per markdownToHtml() run.
let embeddedVideos: Set<string> | null = null;

// Embed URL for a video seen for the first time in this article, else null.
function firstTimeVideoEmbed(url: string): string | null {
  const embed = videoEmbedUrl(url);
  if (!embed) return null;
  if (embeddedVideos) {
    if (embeddedVideos.has(embed)) return null;
    embeddedVideos.add(embed);
  }
  return embed;
}

// Inline markdown -> HTML. Escapes first (XSS-safe), then renders code
// spans, bold, italics, markdown links and bare URLs. Only http(s),
// root-relative, mailto and tel URLs are linkified. When validateLinks is
// true (AI chat replies), links to non-existent help-center articles are
// rendered as plain text instead of broken anchors.
function renderRich(s: string, validateLinks = false): string {
  let html = escape(s);
  // Markdown images. In article bodies (validateLinks=false) render them
  // inline, scaled to the panel, wrapped in a link to the full-size image.
  // In AI chat replies keep the click-out link (the AI can reference images
  // that don't exist, and the chat bubble is too narrow for pictures).
  // MUST run before the link regex below, otherwise that regex would
  // consume the `[alt](url)` portion.
  html = html.replace(
    /!\[([^\]]*)\]\(\s*([^)\s]+)(?:\s+&quot;[^&]*&quot;)?\s*\)/g,
    (_m, alt, url) => {
      // Legacy Intercom imports wrapped some Loom links in image syntax —
      // treat those as videos, not pictures.
      if (!validateLinks) {
        const embed = firstTimeVideoEmbed(url);
        if (embed) return videoIframeHtml(embed);
        // Same video already embedded above — drop the redundant thumbnail.
        if (videoEmbedUrl(url)) return "";
      }
      const safe = /^(https?:\/\/|\/)/i.test(url) ? absUrl(url) : "#";
      const label = alt && alt.trim() ? alt.trim() : "View image";
      if (safe === "#" || validateLinks) {
        return `<a href="${safe}" target="_blank" rel="noopener noreferrer">${label} (image)</a>`;
      }
      return `<a href="${safe}" target="_blank" rel="noopener noreferrer" class="nm-img-link"><img src="${safe}" alt="${alt || ""}" loading="lazy"></a>`;
    },
  );
  html = html.replace(/`([^`]+)`/g, (_m, c) => `<code>${c}</code>`);
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  html = html.replace(
    /\[([^\]]+)\]\(\s*([^)\s]+)(?:\s+&quot;[^&]*&quot;)?\s*\)/g,
    (_m, text, url) => {
      if (validateLinks && isDeadArticleLink(url)) return text;
      // In article bodies, the FIRST link to a Loom/YouTube/Vimeo video
      // becomes an embedded player; repeat links (e.g. per-step timestamp
      // links to the same video) stay ordinary click-out links.
      if (!validateLinks) {
        const embed = firstTimeVideoEmbed(url);
        if (embed) return videoIframeHtml(embed);
      }
      // In-article anchor links: keep the fragment href and don't open a new
      // tab — clicks are intercepted in openArticle() and scrolled in-widget.
      if (/^#/.test(url)) return `<a href="${url}">${text}</a>`;
      const safe = /^(https?:\/\/|\/|mailto:|tel:)/i.test(url) ? absUrl(url) : "#";
      return `<a href="${safe}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  );
  html = html.replace(
    /(^|[\s(])(https?:\/\/[^\s<)]+)/g,
    (_m, pre, url) => {
      if (validateLinks && isDeadArticleLink(url)) return `${pre}${url}`;
      return `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    },
  );
  return html;
}

// GitHub-style heading slug, matching the help-center site's rehype-slug
// output (lowercase, strip punctuation, spaces -> hyphens, collapse/trim
// hyphens). Duplicate slugs within one article get -1, -2… suffixes.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}\p{Pc}\s-]/gu, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function makeSlugger(): (text: string) => string {
  const used = new Map<string, number>();
  return (text: string) => {
    const base = slugify(text);
    const n = used.get(base) ?? 0;
    used.set(base, n + 1);
    return n === 0 ? base : `${base}-${n}`;
  };
}

// Block-level markdown -> HTML for KB article bodies authored in markdown.
// Output is fed through sanitizeHtml() before insertion. Handles headings,
// paragraphs, ordered/unordered lists, blockquotes, fenced code and rules;
// inline formatting is delegated to renderRich().
function markdownToHtml(md: string): string {
  embeddedVideos = new Set();
  // {{video:URL}} shortcodes (used by a couple of articles) become embedded
  // players; unknown/unsupported URLs are dropped, matching the help-center.
  const pre = String(md ?? "").replace(/\{\{video:([^}]+)\}\}/g, (_m, url) => {
    const embed = videoEmbedUrl(String(url).trim());
    return embed ? `\n\n[video](${embed})\n\n` : "";
  });
  const lines = pre.replace(/\r\n/g, "\n").split("\n");
  const slug = makeSlugger();
  const out: string[] = [];
  const code: string[] = [];
  let para: string[] = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;
  const closeLists = (): void => {
    if (inUl) { out.push("</ul>"); inUl = false; }
    if (inOl) { out.push("</ol>"); inOl = false; }
  };
  const flushPara = (): void => {
    if (para.length) { out.push(`<p>${renderRich(para.join(" "))}</p>`); para = []; }
  };
  for (const line of lines) {
    if (/^```/.test(line.trim())) {
      if (inCode) { out.push(`<pre><code>${escape(code.join("\n"))}</code></pre>`); code.length = 0; inCode = false; }
      else { flushPara(); closeLists(); inCode = true; }
      continue;
    }
    if (inCode) { code.push(line); continue; }
    const t = line.trim();
    if (!t) { flushPara(); closeLists(); continue; }
    const h = /^(#{1,6})\s+(.*)$/.exec(t);
    if (h) {
      flushPara(); closeLists();
      const lvl = h[1].length;
      const plain = h[2].replace(/[*_`\[\]()]/g, "").trim();
      const id = slug(plain);
      out.push(`<h${lvl}${id ? ` id="${id}"` : ""}>${renderRich(h[2])}</h${lvl}>`);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) { flushPara(); closeLists(); out.push("<hr>"); continue; }
    const bq = /^>\s?(.*)$/.exec(t);
    if (bq) { flushPara(); closeLists(); out.push(`<blockquote>${renderRich(bq[1])}</blockquote>`); continue; }
    const ol = /^\d+\.\s+(.*)$/.exec(t);
    if (ol) { flushPara(); if (inUl) { out.push("</ul>"); inUl = false; } if (!inOl) { out.push("<ol>"); inOl = true; } out.push(`<li>${renderRich(ol[1])}</li>`); continue; }
    const ul = /^[-*+]\s+(.*)$/.exec(t);
    if (ul) { flushPara(); if (inOl) { out.push("</ol>"); inOl = false; } if (!inUl) { out.push("<ul>"); inUl = true; } out.push(`<li>${renderRich(ul[1])}</li>`); continue; }
    para.push(t);
  }
  flushPara();
  closeLists();
  if (inCode) out.push(`<pre><code>${escape(code.join("\n"))}</code></pre>`);
  return out.join("\n");
}

// DOM-based allow-list sanitizer for KB article HTML. Parses input in a
// detached <template> (no script execution, no resource loading), walks
// the tree, drops disallowed elements/attributes, and re-serializes.
// Replaces the previous regex blacklist which couldn't catch unquoted
// `onerror=`, broken entity encodings, etc.
const ALLOWED_TAGS = new Set([
  "a", "p", "br", "hr", "strong", "em", "b", "i", "u", "code", "pre",
  "blockquote", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "img", "span", "div", "table", "thead", "tbody", "tr", "td", "th",
  "iframe",
]);
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "target", "rel", "class"]),
  div: new Set(["class"]),
  iframe: new Set(["src", "title", "allow", "allowfullscreen", "loading"]),
  img: new Set(["src", "alt", "title", "width", "height", "loading"]),
  h1: new Set(["id"]),
  h2: new Set(["id"]),
  h3: new Set(["id"]),
  h4: new Set(["id"]),
  h5: new Set(["id"]),
  h6: new Set(["id"]),
};
const URL_ATTRS = new Set(["href", "src"]);

// Only class names the widget itself emits survive sanitization.
const ALLOWED_CLASSES = new Set(["nm-video-wrap", "nm-img-link"]);

// iframes may ONLY point at the three known video players' embed URLs.
function isAllowedIframeSrc(value: string): boolean {
  return /^https:\/\/(www\.loom\.com\/embed\/[A-Za-z0-9_-]+|www\.youtube\.com\/embed\/[A-Za-z0-9_-]+|player\.vimeo\.com\/video\/\d+)([/?#]|$)/.test(value.trim());
}

function isSafeUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  // Allow relative + http(s) + mailto + tel + data:image. Reject javascript:,
  // vbscript:, file:, etc.
  if (v.startsWith("javascript:") || v.startsWith("vbscript:") || v.startsWith("data:") && !v.startsWith("data:image/")) return false;
  return /^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/|[^:]*$)/.test(v);
}

function sanitizeNode(node: Node, out: Node): void {
  if (typeof document === "undefined") return;
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(document.createTextNode(child.nodeValue || ""));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as Element;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      // Drop the element but keep its (sanitized) children — preserves text.
      sanitizeNode(el, out);
      return;
    }
    // iframes with a src outside the three allowed video players are
    // dropped entirely (children included — nothing useful inside).
    if (tag === "iframe" && !isAllowedIframeSrc(el.getAttribute("src") || "")) {
      return;
    }
    const safe = document.createElement(tag);
    const allowed = ALLOWED_ATTRS[tag] || new Set<string>();
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (!allowed.has(name)) continue;
      const val = attr.value;
      if (URL_ATTRS.has(name) && !isSafeUrl(val)) continue;
      if (name === "class") {
        const kept = val.split(/\s+/).filter((c) => ALLOWED_CLASSES.has(c)).join(" ");
        if (kept) safe.setAttribute("class", kept);
        continue;
      }
      safe.setAttribute(name, val);
    }
    // In-article anchor links must not open a new tab — they're intercepted
    // and scrolled within the widget.
    if (tag === "a" && (safe.getAttribute("href") || "").startsWith("#")) {
      safe.removeAttribute("target");
    }
    // Force-noopener on external links.
    if (tag === "a" && safe.getAttribute("target") === "_blank") {
      safe.setAttribute("rel", "noopener noreferrer");
    }
    sanitizeNode(el, safe);
    out.appendChild(safe);
  });
}

function sanitizeHtml(html: string): string {
  if (typeof document === "undefined") return "";
  const tpl = document.createElement("template");
  tpl.innerHTML = String(html ?? "");
  const out = document.createElement("div");
  sanitizeNode(tpl.content, out);
  return out.innerHTML;
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

const api: MessengerAPI = (() => {
  const m = new Messenger();
  return {
    boot: (opts) => m.boot(opts),
    shutdown: () => m.shutdown(),
    open: () => m.open(),
    close: () => m.close(),
  };
})();

declare global {
  interface Window {
    NavgarMessenger?: MessengerAPI;
  }
}

if (typeof window !== "undefined") {
  window.NavgarMessenger = api;
}

export default api;
